'use client';

import {
  BodyDefault,
  Button,
  ChatHarProvider,
  Drawer,
  HeadingItem,
  IconButton,
  MetaDefault,
  Spinner,
  TraceView,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import { useAgentExecutionHistory } from '../../hooks/api/useAgentExecutionHistory';
import { useAgentExecutionSSE } from '../../hooks/useAgentExecutionSSE';
import { useHarById } from '../../hooks/api/useHarById';
import { useHarDisplayConfig } from '../../hooks/useHarDisplayConfig';
import { useToolHarPresence } from '../../hooks/useToolHarPresence';
import { useAppTargetId } from '../../store/projectStore';
import { TraceViewSettingsStoreProvider } from '../../providers/TraceViewSettingsStoreProvider';
import { terraProxyApiClient } from '../../clients/testerClient';
import { adaptAgentExecutionHistory } from '../../utils/adaptAgentExecutionHistory';
import {
  canRetryDiscoveryProbe,
  formatDiscoveryDuration,
  formatDiscoveryProbeFailureDetail,
  getAiDiscoveryMetadata,
  getDiscoveryFailureReason,
  resolveDiscoveryProbeDisplayStatus,
  toDiscoveryTraceViewStatus,
} from './discovery.utils';
import { AiDiscoveryStatusBadge } from './AiDiscoveryStatusBadge';
import { AiDiscoveryProbeVerification } from '@terra/ui-ai-applications';

const isLiveProbeStatus = (
  status: AgentExecutionDto['status'] | undefined,
): boolean => status === 'PENDING' || status === 'RUNNING';

const timestampFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

type AiDiscoveryAgentProbeDrawerProps = {
  execution: AgentExecutionDto | null;
  onClose: () => void;
  onRetryProbe?: (candidateId: string) => void;
  isRetryPending?: boolean;
};

export function AiDiscoveryAgentProbeDrawer({
  execution,
  onClose,
  onRetryProbe,
  isRetryPending = false,
}: AiDiscoveryAgentProbeDrawerProps) {
  const t = useTranslations('discovery.aiApplication');
  const tCommon = useTranslations('common');
  const appTargetId = useAppTargetId();
  const executionId = execution?.id ?? '';
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    refetch: refetchHistory,
  } = useAgentExecutionHistory({
    executionId,
    enabled: execution != null,
  });
  const handleTraceEvent = useCallback(() => {
    void refetchHistory();
  }, [refetchHistory]);

  useAgentExecutionSSE({
    executionId,
    enabled: execution != null && Boolean(executionId),
    onTraceEvent: handleTraceEvent,
  });
  const previousStatusRef = useRef(execution?.status);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;

    previousStatusRef.current = execution?.status;

    if (
      !executionId ||
      previousStatus === execution?.status ||
      isLiveProbeStatus(execution?.status)
    ) {
      return;
    }

    void refetchHistory();
  }, [execution?.status, executionId, refetchHistory]);
  const adaptedHistory = useMemo(
    () => (historyData ? adaptAgentExecutionHistory(historyData) : undefined),
    [historyData],
  );
  const hasMessages = (adaptedHistory?.messages?.length ?? 0) > 0;
  const threadId = adaptedHistory?.threadId ?? execution?.id ?? '';
  const { fetchHarById } = useHarById();
  const { hasHarPresence } = useToolHarPresence(threadId);
  const harDisplay = useHarDisplayConfig(appTargetId);
  const fetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }) => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        return response?.hars ?? [];
      } catch (error: unknown) {
        console.error('Failed to fetch HAR data for discovery probe', error);

        return [];
      }
    },
    [],
  );

  if (!execution) {
    return null;
  }

  const metadata = getAiDiscoveryMetadata(execution.metadata);
  const candidateId = metadata?.candidateId;
  const displayStatus = resolveDiscoveryProbeDisplayStatus({ execution });
  const failureDetail = formatDiscoveryProbeFailureDetail(
    getDiscoveryFailureReason({
      metadata,
      runStatus: execution.status,
      statusDescription: execution.statusDescription,
    }),
    t,
  );
  const canRetry =
    onRetryProbe && candidateId && canRetryDiscoveryProbe({ execution });
  const metaLine = [
    metadata?.targetProtocol,
    formatDiscoveryDuration({
      startedAt: execution.createdAt,
      completedAt: execution.completedAt,
      durationMs: execution.duration,
    }),
    timestampFormatter.format(new Date(execution.createdAt)),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Drawer.Root
      open
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
    >
      <Drawer.Backdrop top="10" />
      <Drawer.Positioner w="480px" top="10" bottom="0" h="auto">
        <Drawer.Content
          display="flex"
          flexDirection="column"
          overflowX="hidden"
          h="full"
        >
          <Drawer.Header px="3" py="3">
            <HStack
              justifyContent="space-between"
              w="full"
              gap="2"
              alignItems="flex-start"
            >
              <VStack alignItems="flex-start" gap="1" minW="0" flex="1">
                <HStack gap="2" alignItems="center" minW="0" w="full">
                  <HeadingItem truncate>{t('executionTitle')}</HeadingItem>
                  <Box flexShrink={0}>
                    <AiDiscoveryStatusBadge status={displayStatus} />
                  </Box>
                </HStack>
                {metaLine ? (
                  <MetaDefault color="fg.muted" truncate>
                    {metaLine}
                  </MetaDefault>
                ) : null}
              </VStack>
              <HStack gap="1" flexShrink={0}>
                {canRetry ? (
                  <Button
                    size="xs"
                    variant="outline"
                    loading={isRetryPending}
                    onClick={() => {
                      onRetryProbe(candidateId);
                    }}
                  >
                    {t('retry')}
                  </Button>
                ) : null}
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={tCommon('close')}
                  onClick={onClose}
                >
                  <Cancel01Icon size={16} />
                </IconButton>
              </HStack>
            </HStack>
          </Drawer.Header>
          <Drawer.Body px="3" py="3" flex="1" minH="0" overflow="hidden">
            <VStack gap="3" alignItems="stretch" h="full" minH="0">
              {failureDetail ? (
                <BodyDefault color="fg.error">{failureDetail}</BodyDefault>
              ) : null}
              <Box maxH="240px" overflowY="auto" flexShrink={0}>
                <AiDiscoveryProbeVerification
                  results={metadata?.verificationResults ?? []}
                  rollup={metadata?.verificationRollup}
                  labels={{
                    title: t('verificationTitle'),
                    rollup: t('verificationRollup', {
                      recorded: metadata?.verificationRollup?.recorded ?? 0,
                      confirmed: metadata?.verificationRollup?.confirmed ?? 0,
                      contradicted:
                        metadata?.verificationRollup?.contradicted ?? 0,
                      unverified: metadata?.verificationRollup?.unverified ?? 0,
                    }),
                    claim: t('verificationClaim'),
                    stimulus: t('verificationStimulus'),
                    predictedSignal: t('verificationPredicted'),
                    observedResponse: t('verificationObserved'),
                    confirmed: t('verificationConfirmed'),
                    contradicted: t('verificationContradicted'),
                    emptyObserved: t('verificationEmptyObserved'),
                    invocation: t('verificationInvocation'),
                    invocationHidden: t('verificationInvocationHidden'),
                    invocationUserVisible: t(
                      'verificationInvocationUserVisible',
                    ),
                    invocationUnknown: t('verificationInvocationUnknown'),
                    exchange: t('verificationExchange'),
                  }}
                />
              </Box>
              {isHistoryLoading ? (
                <HStack w="full" justifyContent="center" py="8">
                  <Spinner size="sm" />
                  <MetaDefault color="fg.muted">
                    {tCommon('loading')}
                  </MetaDefault>
                </HStack>
              ) : null}
              {isHistoryError ? (
                <BodyDefault color="fg.error">{tCommon('error')}</BodyDefault>
              ) : null}
              {!isHistoryLoading &&
              !isHistoryError &&
              adaptedHistory &&
              hasMessages ? (
                <Box flex="1" minH="0" overflow="hidden">
                  <TraceViewSettingsStoreProvider>
                    <ChatHarProvider
                      threadId={threadId || null}
                      fetchHarData={fetchHarData}
                      fetchHarById={fetchHarById}
                      hasHarPresence={hasHarPresence}
                      harDisplay={harDisplay}
                    >
                      <TraceView
                        history={adaptedHistory}
                        maxHeight="100%"
                        status={toDiscoveryTraceViewStatus(execution.status)}
                        showHeader={false}
                        showToolbar={false}
                        forcedViewMode="full"
                        tracePhase="ai-discovery"
                        preferHarOverToolOutput
                      />
                    </ChatHarProvider>
                  </TraceViewSettingsStoreProvider>
                </Box>
              ) : null}
              {!isHistoryLoading &&
              !isHistoryError &&
              (!adaptedHistory || !hasMessages) ? (
                <HStack w="full" justifyContent="center" py="8">
                  <BodyDefault color="fg.muted">
                    {t('noProbeHistory')}
                  </BodyDefault>
                </HStack>
              ) : null}
            </VStack>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
